/**
 * Centralized Design Tokens
 *
 * This file provides a single source of truth for non-color visual properties.
 * These tokens ensure consistency across the application.
 *
 * USAGE RULES:
 * - Use these tokens for programmatic styling when needed
 * - Prefer Tailwind utility classes for most styling needs
 * - Colors should ONLY be accessed via useThemeColors() hook
 */

/**
 * Spacing Scale
 * Based on 8px grid system for consistent visual rhythm
 * Use Tailwind classes (p-4, gap-6, mb-3) for most cases
 * Scales with --sizing-multiplier CSS variable
 */
export const spacing = {
  xs: 'calc(8px * var(--sizing-multiplier, 1))',
  sm: 'calc(12px * var(--sizing-multiplier, 1))',
  md: 'calc(16px * var(--sizing-multiplier, 1))',
  lg: 'calc(24px * var(--sizing-multiplier, 1))',
  xl: 'calc(32px * var(--sizing-multiplier, 1))',
  '2xl': 'calc(48px * var(--sizing-multiplier, 1))',
  '3xl': 'calc(64px * var(--sizing-multiplier, 1))',
} as const;

/**
 * Sizing Tokens
 * Consistent dimensions for UI elements
 * Scales with --sizing-multiplier CSS variable
 */
export const sizing = {
  /** Icon sizing - use for consistent icon dimensions */
  icon: {
    sm: 'calc(16px * var(--sizing-multiplier, 1))', // Inline text icons
    md: 'calc(20px * var(--sizing-multiplier, 1))', // Standard UI icons
    lg: 'calc(24px * var(--sizing-multiplier, 1))', // Emphasis icons, headers
    xl: 'calc(32px * var(--sizing-multiplier, 1))', // Hero sections, large features
  },
  /** Button heights - ensures proper touch targets */
  button: {
    sm: 'calc(32px * var(--sizing-multiplier, 1))', // Compact contexts
    md: 'calc(40px * var(--sizing-multiplier, 1))', // Standard buttons
    lg: 'calc(44px * var(--sizing-multiplier, 1))', // Emphasis, CTAs
    xl: 'calc(48px * var(--sizing-multiplier, 1))', // Hero sections
  },
  /** Input heights - consistent form element sizing */
  input: {
    sm: 'calc(36px * var(--sizing-multiplier, 1))',
    md: 'calc(40px * var(--sizing-multiplier, 1))',
    lg: 'calc(44px * var(--sizing-multiplier, 1))',
  },
} as const;

/**
 * Border Radius Scale
 * Mixed radii create visual interest and component hierarchy
 * Scales subtly (50% effect) with --sizing-multiplier CSS variable
 */
export const radius = {
  sm: 'calc(6px * (1 + (var(--sizing-multiplier, 1) - 1) * 0.5))', // Small elements, badges
  md: 'calc(8px * (1 + (var(--sizing-multiplier, 1) - 1) * 0.5))', // Inputs, dropdowns
  DEFAULT: 'calc(10px * (1 + (var(--sizing-multiplier, 1) - 1) * 0.5))', // Buttons, standard elements
  lg: 'calc(14px * (1 + (var(--sizing-multiplier, 1) - 1) * 0.5))', // Cards, columns
  xl: 'calc(16px * (1 + (var(--sizing-multiplier, 1) - 1) * 0.5))', // Large cards, prominent surfaces
  '2xl': 'calc(20px * (1 + (var(--sizing-multiplier, 1) - 1) * 0.5))', // Modals, dialogs
  full: '9999px', // Pill shapes, fully rounded
} as const;

/**
 * Typography Scale
 * Modern font system with proper weight distribution
 */
export const typography = {
  /** Font weights - Inter variable font weights */
  weights: {
    normal: 400, // Body text
    medium: 500, // UI elements
    semibold: 600, // Headings
    bold: 700, // Emphasis
  },
  /** Font sizes - use Tailwind classes (text-sm, text-lg) for most cases */
  sizes: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
  },
  /** Line heights - for proper vertical rhythm */
  lineHeights: {
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
  },
} as const;

/**
 * Shadow Tokens
 * Layered shadows for natural depth without harsh borders
 * Use Tailwind classes (shadow-soft, shadow-soft-md) for most cases
 */
export const shadows = {
  soft: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
  'soft-md':
    '0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04)',
  'soft-lg':
    '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)',
  'soft-xl':
    '0 20px 25px -5px rgba(0, 0, 0, 0.08), 0 10px 10px -5px rgba(0, 0, 0, 0.02)',
  elevated:
    '0 12px 24px -4px rgba(0, 0, 0, 0.12), 0 8px 12px -4px rgba(0, 0, 0, 0.04)',
  /** Interactive element elevation on hover/active */
  interactive: '0 8px 20px rgba(0, 0, 0, 0.2)',
} as const;

/**
 * Opacity Scale
 * Standard opacity values for backgrounds, overlays, and transparency
 * Use with withOpacity() helper or Tailwind /opacity syntax
 */
export const opacity = {
  subtle: 0.05, // 5% - Very subtle tint
  light: 0.1, // 10% - Light tint
  soft: 0.15, // 15% - Soft tint (error backgrounds)
  medium: 0.2, // 20% - Medium tint (focus rings, badges)
  strong: 0.4, // 40% - Strong tint (selected states, shadows)
  heavy: 0.6, // 60% - Heavy tint
  disabled: 0.5, // 50% - Disabled state
} as const;

/**
 * Focus Ring Tokens
 * Consistent focus indicators for accessibility and visual feedback
 * Note: Colors should be applied dynamically using theme colors
 */
export const focusRing = {
  /** Standard focus ring width and offset */
  width: '3px',
  offset: '0px',
  /** Focus ring opacity (applied to theme colors) */
  opacity: '20%',
} as const;

/**
 * Layout Dimensions
 * Base values for major layout components
 * Scales with --sizing-multiplier CSS variable
 */
export const layout = {
  header: {
    height: 'calc(64px * var(--sizing-multiplier, 1))',
    logo: 'calc(40px * var(--sizing-multiplier, 1))',
  },
  sidebar: {
    width: 'calc(256px * var(--sizing-multiplier, 1))',
    collapsed: 'calc(80px * var(--sizing-multiplier, 1))',
  },
  kanban: {
    columnWidth: 'calc(320px * var(--sizing-multiplier, 1))',
    columnCollapsed: 'calc(48px * var(--sizing-multiplier, 1))',
    columnGap: 'calc(16px * var(--sizing-multiplier, 1))',
    cardHeight: 'calc(140px * var(--sizing-multiplier, 1))',
    containerPadding: 'calc(48px * var(--sizing-multiplier, 1))',
  },
  /** Content width presets — used by AppLayout's ContentLayout system */
  contentWidth: {
    narrow: '600px',
    medium: '896px',
    wide: '1200px',
  },
  moodWidget: {
    height: 'calc(80px * var(--sizing-multiplier, 1))',
    minWidth: 'calc(100px * var(--sizing-multiplier, 1))',
  },
} as const;

/**
 * Color Usage Note
 *
 * DO NOT export or use colors from this file.
 * Colors should ONLY be accessed via the useThemeColors() hook.
 *
 * This ensures:
 * - Consistent theming across the app
 * - Dynamic theme switching without page refresh
 * - Single source of truth for color values
 *
 * Example:
 * ```tsx
 * const colors = useThemeColors();
 * <div style={{ backgroundColor: colors.primary }}>
 * ```
 */
